import React, { useState, useEffect, useCallback, useRef } from 'react';
import gateApi from '../api';
import {
  C, FormShell, Field, Select, TextArea, SubmitButton, Banner,
} from './FormKit';

const REJECT_REASONS = [
  { value: '', label: '— select a reason —' },
  { value: 'NEVER_EXITED', label: 'Vehicle never exited — stale/abandoned record' },
  { value: 'DUPLICATE_ENTRY', label: 'Duplicate entry (real trip logged separately)' },
  { value: 'WRONG_ENTRY', label: 'Entered by mistake / test entry' },
  { value: 'UNAUTHORIZED_VEHICLE', label: 'Vehicle not authorized for this site' },
  { value: 'DOCUMENTATION_ISSUE', label: 'Documentation / paperwork issue' },
  { value: 'OTHER', label: 'Other (explain below)' },
];

function fmtDuration(mins) {
  if (mins == null) return '—';
  const h = Math.floor(mins / 60), m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// Admin-only. Reachable from the header avatar menu, not a dashboard tab —
// finds any vehicle still short of challan/payment (however old/stuck,
// including one that turns out to be going empty with nothing to weigh or
// bill) and rejects it with a mandatory structured reason + free-text
// details, fully audited.
export default function RejectVehicle() {
  const [vehicles, setVehicles] = useState([]);
  const [selTx, setSelTx]       = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const [reasonCode, setReasonCode] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [busy, setBusy]     = useState(false);
  const [result, setResult] = useState(null);

  const selected = vehicles.find(v => v.transactionId === selTx);

  const loadVehicles = useCallback(async () => {
    try { setVehicles(await gateApi.getRejectableVehicles()); }
    catch { /* keep last list if fetch fails */ }
  }, []);

  useEffect(() => {
    loadVehicles();
    const interval = setInterval(loadVehicles, 15000);
    return () => clearInterval(interval);
  }, [loadVehicles]);

  useEffect(() => {
    function onClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const filtered = vehicles.filter(v =>
    v.licensePlate?.toUpperCase().includes(searchQuery.toUpperCase().trim())
  );

  function selectVehicle(v) {
    setSelTx(v.transactionId);
    setSearchQuery(v.licensePlate || '');
    setShowDropdown(false);
    setResult(null);
  }

  function clearSelection() {
    setSelTx(''); setSearchQuery('');
    setReasonCode(''); setReasonText('');
    setResult(null);
  }

  async function submit() {
    if (!selTx) { setResult({ ok: false, msg: 'Select a vehicle first.' }); return; }
    if (!reasonCode) { setResult({ ok: false, msg: 'Pick a reason.' }); return; }
    if (reasonCode === 'OTHER' && !reasonText.trim()) {
      setResult({ ok: false, msg: 'Explain the reason below.' }); return;
    }

    setBusy(true); setResult(null);
    try {
      const r = await gateApi.rejectTransaction(selTx, { reasonCode, reasonText });
      setResult({ ok: true, msg: `${r.licensePlate || 'Vehicle'} rejected.` });
      clearSelection();
      loadVehicles();
    } catch (e) {
      const d = e.response?.data;
      setResult({ ok: false, msg: d?.message || d?.error || 'Could not reject vehicle.' });
    } finally {
      setBusy(false);
    }
  }

  let minsInside = selected?.minutesInside;
  if (selected?.entryTime) {
    minsInside = Math.max(0, Math.round((Date.now() - new Date(selected.entryTime)) / 60000));
  }

  return (
    <FormShell title="Reject vehicle" accent={C.red}>
      <h1 style={{ fontSize: 26, fontWeight: 700, margin: '0 0 4px' }}>Reject a vehicle</h1>
      <p style={{ color: C.muted, fontSize: 14, margin: '0 0 26px' }}>
        Find any currently-active vehicle and reject it with a reason. Admin only, fully audited.
      </p>

      {result && <Banner type={result.ok ? 'success' : 'error'}>{result.msg}</Banner>}

      <Field label="Find vehicle"
             hint={vehicles.length ? `${vehicles.length} vehicle(s) eligible for rejection` : 'No vehicles currently eligible'}>
        <div ref={dropdownRef} style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder={vehicles.length ? 'Type to search number plate…' : 'none active'}
            disabled={!vehicles.length}
            value={searchQuery}
            onFocus={() => setShowDropdown(true)}
            onChange={(e) => { setSearchQuery(e.target.value); setSelTx(''); }}
            style={{
              width: '100%', boxSizing: 'border-box', background: C.panel2,
              border: `1px solid ${C.border}`, borderRadius: 10, color: C.text,
              padding: '14px 16px', fontSize: 16, fontFamily: C.mono, outline: 'none',
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}
          />
          {showDropdown && vehicles.length > 0 && (
            <div style={{
              position: 'absolute', top: '102%', left: 0, right: 0,
              background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10,
              maxHeight: 220, overflowY: 'auto', zIndex: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)', padding: '6px 0',
            }}>
              {filtered.length > 0 ? filtered.map(v => (
                <div
                  key={v.transactionId}
                  onClick={() => selectVehicle(v)}
                  style={{
                    padding: '12px 16px', cursor: 'pointer', fontSize: 14,
                    fontFamily: C.sans, color: C.text, display: 'flex',
                    justifyContent: 'space-between', transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = C.panel2; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ fontFamily: C.mono, fontWeight: 700 }}>{v.licensePlate}</span>
                  <span style={{ color: C.muted, fontSize: 12 }}>
                    {v.vehicleType || 'VEHICLE'} · {fmtDuration(v.minutesInside)} inside
                  </span>
                </div>
              )) : (
                <div style={{ padding: '14px 16px', color: C.faint, fontSize: 13, fontFamily: C.sans }}>
                  No matching vehicles found
                </div>
              )}
            </div>
          )}
        </div>
      </Field>

      {selected && (
        <div style={{
          background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10,
          padding: '12px 16px', marginBottom: 20, display: 'flex',
          gap: 24, flexWrap: 'wrap', fontSize: 13,
        }}>
          <span style={{ color: C.muted }}>Entered:&nbsp;
            <span style={{ color: C.text, fontFamily: C.mono }}>
              {selected.entryTime ? new Date(selected.entryTime).toLocaleString() : '—'}</span>
          </span>
          <span style={{ color: C.muted }}>Time inside:&nbsp;
            <span style={{ color: C.red, fontFamily: C.mono }}>{fmtDuration(minsInside)}</span>
          </span>
          {selected.expectedLoad && (
            <span style={{ color: C.muted }}>Expected:&nbsp;
              <span style={{ color: C.text }}>{selected.expectedLoad}</span>
            </span>
          )}
        </div>
      )}

      {selected && (
        <>
          <Field label="Reason (required)">
            <Select value={reasonCode} onChange={setReasonCode} options={REJECT_REASONS} />
          </Field>

          <Field label={`Details ${reasonCode === 'OTHER' ? '(required)' : '(optional)'}`}>
            <TextArea value={reasonText} onChange={setReasonText} placeholder="Additional details…" />
          </Field>

          <SubmitButton onClick={submit} disabled={busy} color={C.red}>
            {busy ? 'Rejecting…' : 'Confirm reject'}
          </SubmitButton>

          <button onClick={clearSelection} style={{
            marginTop: 14, width: '100%', background: 'none', border: 'none',
            color: C.faint, fontFamily: C.mono, fontSize: 12, cursor: 'pointer',
            letterSpacing: '0.08em',
          }}>CLEAR SELECTION</button>
        </>
      )}
    </FormShell>
  );
}
